import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, FileText, ClockCountdown, CheckCircle, Users, CaretRight, PaperPlaneTilt,
  Stack, Trash, ArrowRight, Warning, Spinner, Folder, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { NewFolderButton, FolderTiles, FolderBar, ItemMenu, FolderNameDialog, MoveDialog } from "@/components/Folders";

const StatusPill = ({ status, progress }) => {
  const map = {
    completed: { label: "Completed", cls: "bg-success/10 text-success border-success/30" },
    sent: { label: progress != null ? `Awaiting ${progress}` : "In Progress", cls: "bg-action/10 text-action border-action/30" },
    voided: { label: "Voided", cls: "bg-slate-100 text-slate-500 border-slate-300" },
    declined: { label: "Declined", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  };
  const s = map[status] || map.sent;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${s.cls}`}>
      {s.label}
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</span>
      <Icon size={20} weight="duotone" style={{ color: tint }} />
    </div>
    <div className="mt-3 font-heading text-4xl font-bold tracking-tight text-navy">{value}</div>
  </div>
);

export default function Dashboard() {
  const [envelopes, setEnvelopes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [tplDeleteTarget, setTplDeleteTarget] = useState(null);
  const [deletingTpl, setDeletingTpl] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [folders, setFolders] = useState([]);
  const [openFolder, setOpenFolder] = useState({ template: null, document: null });
  const [nameDialog, setNameDialog] = useState(null); // { mode: create|rename, kind, folder?, thenMove? }
  const [moveTarget, setMoveTarget] = useState(null); // { kind, id, label, folder_id }

  useEffect(() => {
    Promise.all([
      api.get("/envelopes").then((r) => setEnvelopes(r.data)),
      api.get("/templates").then((r) => setTemplates(r.data)),
      api.get("/folders").then((r) => setFolders(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const itemsOf = (kind) => (kind === "template" ? templates : envelopes);
  const foldersOf = (kind) => folders
    .filter((f) => f.kind === kind)
    .map((f) => ({ ...f, item_count: itemsOf(kind).filter((i) => i.folder_id === f.id).length }));
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const matches = (i) => [i.name, i.title, i.document_name, ...(i.recipients || []).flatMap((r) => [r.name, r.email])]
    .some((v) => v && String(v).toLowerCase().includes(q));
  const visible = (kind) => (q
    ? itemsOf(kind).filter(matches)
    : itemsOf(kind).filter((i) => (openFolder[kind] ? i.folder_id === openFolder[kind] : !i.folder_id)));
  const folderName = (id) => folders.find((f) => f.id === id)?.name;
  const dragProps = (kind, id) => ({
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.setData(`application/x-camino-${kind}`, id); e.dataTransfer.effectAllowed = "move"; },
  });

  const moveItem = async (kind, id, folderId, folderName) => {
    try {
      await api.patch(`/${kind === "template" ? "templates" : "envelopes"}/${id}/folder`, { folder_id: folderId });
      const set = kind === "template" ? setTemplates : setEnvelopes;
      set((xs) => xs.map((x) => (x.id === id ? { ...x, folder_id: folderId } : x)));
      const fname = folderName || folders.find((f) => f.id === folderId)?.name;
      toast.success(folderId ? `Moved to "${fname}"` : "Moved back to main list");
      setMoveTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not move item");
    }
  };

  const saveFolderName = async (name) => {
    try {
      if (nameDialog.mode === "rename") {
        const r = await api.patch(`/folders/${nameDialog.folder.id}`, { name });
        setFolders((fs) => fs.map((f) => (f.id === r.data.id ? { ...f, name: r.data.name } : f)));
        toast.success("Folder renamed");
      } else {
        const r = await api.post("/folders", { name, kind: nameDialog.kind });
        setFolders((fs) => [...fs, r.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Folder "${r.data.name}" created`);
        if (nameDialog.thenMove) {
          await moveItem(nameDialog.kind, nameDialog.thenMove.id, r.data.id, r.data.name);
        }
      }
      setNameDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save folder");
    }
  };

  const deleteFolder = async (folder) => {
    try {
      await api.delete(`/folders/${folder.id}`);
      setFolders((fs) => fs.filter((f) => f.id !== folder.id));
      setOpenFolder((o) => ({ ...o, [folder.kind]: null }));
      toast.success("Folder deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete folder");
    }
  };

  const startMove = (kind, item) => {
    const target = { kind, id: item.id, label: item.name || item.title, folder_id: item.folder_id || null };
    if (!foldersOf(kind).length) setNameDialog({ mode: "create", kind, thenMove: target });
    else setMoveTarget(target);
  };

  const sectionFolderUI = (kind, label) => {
    if (q) return null;
    const fs = foldersOf(kind);
    const open = fs.find((f) => f.id === openFolder[kind]);
    return open ? (
      <FolderBar folder={open} label={label}
        onBack={() => setOpenFolder((o) => ({ ...o, [kind]: null }))}
        onRename={() => setNameDialog({ mode: "rename", kind, folder: open })}
        onDelete={() => deleteFolder(open)} />
    ) : (
      <FolderTiles folders={fs} kind={kind} onOpen={(id) => setOpenFolder((o) => ({ ...o, [kind]: id }))}
        onDropItem={(id, fid) => { if (itemsOf(kind).find((i) => i.id === id)?.folder_id !== fid) moveItem(kind, id, fid); }} />
    );
  };

  const FolderBadge = ({ id }) => (q && id && folderName(id) ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#D0DFE1] text-[#292F34] px-2 py-0.5 text-[11px] font-semibold shrink-0" data-testid="search-folder-badge">
      <Folder size={11} weight="fill" /> {folderName(id)}
    </span>
  ) : null);

  const openDelete = (e, env) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setDeleteTarget(env);
    setConfirmEmail("");
  };

  const emailMatches = confirmEmail.trim().toLowerCase() === (user?.email || "").trim().toLowerCase();

  const confirmDelete = async () => {
    if (!deleteTarget || !emailMatches) return;
    setDeleting(true);
    try {
      await api.delete(`/envelopes/${deleteTarget.id}`, { data: { confirm_email: confirmEmail.trim() } });
      setEnvelopes((es) => es.filter((e) => e.id !== deleteTarget.id));
      toast.success("Document deleted");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete document");
    } finally {
      setDeleting(false);
    }
  };

  const deleteTemplate = async (e, tid) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setTplDeleteTarget(tid);
  };

  const confirmDeleteTemplate = async () => {
    if (!tplDeleteTarget) return;
    setDeletingTpl(true);
    try {
      await api.delete(`/templates/${tplDeleteTarget}`);
      setTemplates((ts) => ts.filter((t) => t.id !== tplDeleteTarget));
      toast.success("Template deleted");
      setTplDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete template");
    } finally {
      setDeletingTpl(false);
    }
  };

  const signerProgress = (env) => {
    const signers = env.recipients.filter((r) => r.role === "signer");
    const done = signers.filter((r) => r.status === "signed").length;
    return { done, total: signers.length };
  };

  const total = envelopes.length;
  const completed = envelopes.filter((e) => e.status === "completed").length;
  const inProgress = envelopes.filter((e) => e.status === "sent").length;
  const recipients = envelopes.reduce((a, e) => a + e.recipients.length, 0);

  return (
    <div className="min-h-screen camino-dark">
      <TopNav>
        <Button onClick={() => navigate("/new")} data-testid="new-envelope-btn" className="rounded-full bg-navy hover:bg-navy/90 gap-2 h-10 px-4 sm:px-5 shrink-0">
          <Plus size={18} weight="bold" /> <span className="hidden sm:inline">New Document</span>
        </Button>
      </TopNav>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <div className="animate-fade-up">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-action">Send &amp; Track</p>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-navy leading-none">
            Your signing dashboard
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl leading-relaxed">
            Prepare a document, place fields, and send it securely. Track every signature in real time.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-up" style={{ animationDelay: "60ms" }}>
          <Stat icon={FileText} label="Total" value={total} tint="#1A2B4C" />
          <Stat icon={ClockCountdown} label="In Progress" value={inProgress} tint="#0052FF" />
          <Stat icon={CheckCircle} label="Completed" value={completed} tint="#2A9D8F" />
          <Stat icon={Users} label="Recipients" value={recipients} tint="#F4A261" />
        </div>

        <div className="mt-8 relative animate-fade-up" style={{ animationDelay: "90ms" }}>
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="dashboard-search-input"
            placeholder="Search documents and templates by title, file, signer name or email"
            className="h-12 rounded-full pl-11 pr-11 bg-white" />
          {query && (
            <button onClick={() => setQuery("")} data-testid="dashboard-search-clear" title="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
        {q && (
          <p className="mt-3 text-sm text-slate-500" data-testid="search-summary">
            {visible("document").length} document{visible("document").length !== 1 ? "s" : ""} and {visible("template").length} template{visible("template").length !== 1 ? "s" : ""} match “{query.trim()}”, including inside folders.
          </p>
        )}

        {!loading && (
          <div className="mt-10" data-testid="templates-section">
            <div className="flex items-center gap-2 mb-4">
              <Stack size={20} weight="duotone" className="text-navy" />
              <h2 className="font-heading text-lg font-bold tracking-tight text-navy">Templates</h2>
              <span className="ml-auto"><NewFolderButton testId="new-template-folder-btn" onClick={() => setNameDialog({ mode: "create", kind: "template" })} /></span>
            </div>
            {sectionFolderUI("template", "templates")}
            {visible("template").length === 0 && (
              <p className="text-sm text-slate-400 py-4" data-testid="templates-empty">
                {q ? "No templates match your search." : openFolder.template ? "This folder is empty. Drag a template onto it or use its ⋮ menu." : templates.length ? "All templates are in folders." : "No templates yet. Use \"Save as template\" on any document to reuse it later."}
              </p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visible("template").map((t) => (
                <div key={t.id} data-testid={`template-card-${t.id}`} {...dragProps("template", t.id)}
                  className="group rounded-xl border border-slate-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-navy/30 cursor-grab active:cursor-grabbing">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-navy truncate">{t.name}</h3>
                      <p className="text-xs text-slate-400 truncate">{t.document_name} · {t.fields.length} field{t.fields.length !== 1 ? "s" : ""}</p>
                      <div className="mt-1.5 empty:hidden"><FolderBadge id={t.folder_id} /></div>
                    </div>
                    <ItemMenu testId={`template-menu-${t.id}`} inFolder={!!t.folder_id} hasFolders={foldersOf("template").length > 0}
                      onEdit={() => navigate(`/new?template=${t.id}&edit=1`)}
                      onMove={() => startMove("template", t)}
                      onRemoveFromFolder={() => moveItem("template", t.id, null)}
                      onDelete={() => deleteTemplate(null, t.id)} deleteLabel="Delete template" />
                  </div>
                  <Button onClick={() => navigate(`/new?template=${t.id}`)} data-testid={`use-template-${t.id}`}
                    variant="outline" className="mt-3 w-full rounded-full gap-2 text-navy group-hover:border-navy/40">
                    Use template <ArrowRight size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} weight="duotone" className="text-navy" />
            <h2 className="font-heading text-lg font-bold tracking-tight text-navy">Documents</h2>
            <span className="ml-auto"><NewFolderButton testId="new-document-folder-btn" onClick={() => setNameDialog({ mode: "create", kind: "document" })} /></span>
          </div>
          {!loading && sectionFolderUI("document", "documents")}

          {loading ? (
            <div className="py-24 text-center text-slate-400">Loading…</div>
          ) : envelopes.length === 0 ? (
            <div
              className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white"
              data-testid="empty-state"
            >
              <div
                className="absolute inset-0 opacity-[0.12]"
                style={{
                  backgroundImage: "url(https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
                  backgroundSize: "cover", backgroundPosition: "center",
                }}
              />
              <div className="relative px-8 py-20 text-center">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-navy flex items-center justify-center">
                  <PaperPlaneTilt size={26} weight="duotone" color="#fff" />
                </div>
                <h3 className="mt-5 font-heading text-2xl font-bold tracking-tight text-navy">No documents yet</h3>
                <p className="mt-2 text-slate-500">Upload your first agreement to get started.</p>
                <Button onClick={() => navigate("/new")} className="mt-6 rounded-full bg-action hover:bg-action/90 gap-2 h-11 px-6" data-testid="empty-new-btn">
                  <Plus size={18} weight="bold" /> Create Document
                </Button>
              </div>
            </div>
          ) : visible("document").length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="documents-empty">
              {q ? "No documents match your search." : openFolder.document ? "This folder is empty. Drag a document onto it or use its ⋮ menu." : "All documents are in folders."}
            </p>
          ) : (
            <div className="grid gap-3">
              {visible("document").map((env, idx) => {
                const p = signerProgress(env);
                const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
                return (
                  <Link
                    key={env.id}
                    to={`/envelope/${env.id}`}
                    data-testid={`envelope-row-${env.id}`}
                    {...dragProps("document", env.id)}
                    className="group flex items-center gap-2.5 sm:gap-4 min-w-0 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-navy/30 animate-fade-up"
                    style={{ animationDelay: `${idx * 40}ms` }}
                  >
                    <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText size={20} weight="duotone" className="text-navy sm:hidden" />
                      <FileText size={22} weight="duotone" className="text-navy hidden sm:block" />
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-semibold text-navy truncate min-w-0 flex-1">{env.title}</h3>
                        <FolderBadge id={env.folder_id} />
                        <span className="shrink-0"><StatusPill status={env.status} progress={env.status === "sent" ? `${p.done}/${p.total}` : null} /></span>
                      </div>
                      <p className="text-sm text-slate-400 truncate mt-0.5">
                        {env.document_name} · {env.recipients.length} recipient{env.recipients.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="hidden sm:flex flex-col items-end w-32">
                      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: env.status === "completed" ? "#2A9D8F" : "#0052FF" }} />
                      </div>
                      <span className="mt-1 text-[11px] text-slate-400">{p.done}/{p.total} signed</span>
                    </div>
                    <ItemMenu testId={`envelope-menu-${env.id}`} inFolder={!!env.folder_id} hasFolders={foldersOf("document").length > 0}
                      onMove={() => startMove("document", env)}
                      onRemoveFromFolder={() => moveItem("document", env.id, null)}
                      onDelete={() => openDelete(null, env)} deleteLabel="Delete document" />
                    <CaretRight size={18} className="text-slate-300 group-hover:text-navy transition-colors shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-envelope-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
              <Warning size={22} weight="fill" className="text-destructive" /> Delete document
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            This permanently deletes <span className="font-semibold text-navy">{deleteTarget?.title}</span> and its
            audit trail. Signers can no longer open their link. This cannot be undone.
          </p>
          <div className="mt-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Type your email <span className="font-mono normal-case text-slate-600">{user?.email}</span> to confirm
            </Label>
            <Input
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user?.email}
              className="mt-2 h-11"
              data-testid="delete-confirm-email-input"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-btn">Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={!emailMatches || deleting}
              className="rounded-full bg-destructive hover:bg-destructive/90 gap-2"
              data-testid="delete-confirm-btn"
            >
              {deleting ? <Spinner size={16} className="animate-spin" /> : <Trash size={16} weight="bold" />} Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FolderNameDialog open={!!nameDialog}
        title={nameDialog?.mode === "rename" ? "Rename folder" : `New ${nameDialog?.kind === "template" ? "template" : "document"} folder`}
        initial={nameDialog?.folder?.name || ""} onClose={() => setNameDialog(null)} onSave={saveFolderName} />

      <MoveDialog target={moveTarget} folders={moveTarget ? foldersOf(moveTarget.kind) : []}
        onClose={() => setMoveTarget(null)}
        onMove={(fid) => moveItem(moveTarget.kind, moveTarget.id, fid)}
        onNewFolder={() => { setNameDialog({ mode: "create", kind: moveTarget.kind, thenMove: moveTarget }); setMoveTarget(null); }} />

      <Dialog open={!!tplDeleteTarget} onOpenChange={(o) => !o && setTplDeleteTarget(null)}>
        <DialogContent data-testid="delete-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
              <Warning size={22} weight="fill" className="text-destructive" /> Delete template
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Are you sure you want to delete this template? Documents you already sent from it are not affected. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTplDeleteTarget(null)} data-testid="delete-template-cancel-btn">Cancel</Button>
            <Button
              onClick={confirmDeleteTemplate}
              disabled={deletingTpl}
              className="rounded-full bg-destructive hover:bg-destructive/90 gap-2"
              data-testid="delete-template-confirm-btn"
            >
              {deletingTpl ? <Spinner size={16} className="animate-spin" /> : <Trash size={16} weight="bold" />} Delete template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
